/**
 * Fired on `window` when the support chat widget could not be opened because
 * the Tawk.to script was still loading or was blocked.
 * An in-page notice listens for this so the lead is never lost silently.
 */
export const SUPPORT_CHAT_UNAVAILABLE_EVENT = 'goldwarden:support-chat-unavailable';

const POLL_INTERVAL_MS = 200;
const MAX_ATTEMPTS = 15; // ~3 seconds

function tryOpen(): boolean {
  const Tawk_API = (window as any).Tawk_API;
  if (Tawk_API && typeof Tawk_API.maximize === 'function') {
    Tawk_API.maximize();
    return true;
  }
  return false;
}

/**
 * Opens the Tawk.to support chat widget.
 *
 * The Tawk.to script is loaded asynchronously by the useTawkTo hook, so on a
 * slow or blocked connection its API may not exist yet when a visitor clicks one
 * of our "Start the chat" buttons. Instead of failing silently we briefly poll
 * for the API, and if it never shows up we broadcast a friendly in-page notice.
 *
 * Resolves `true` when the widget was opened, `false` when it stayed
 * unreachable. The returned promise is safe to ignore for fire-and-forget
 * button handlers.
 */
export async function openSupportChat(): Promise<boolean> {
  if (tryOpen()) {
    return true;
  }

  return new Promise<boolean>((resolve) => {
    let attempts = 0;

    const poll = () => {
      if (tryOpen()) {
        resolve(true);
        return;
      }

      attempts += 1;

      if (attempts >= MAX_ATTEMPTS) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(SUPPORT_CHAT_UNAVAILABLE_EVENT));
        }
        resolve(false);
        return;
      }

      window.setTimeout(poll, POLL_INTERVAL_MS);
    };

    window.setTimeout(poll, POLL_INTERVAL_MS);
  });
}